import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './user/pages/LoginPage';
import FirstLogin from './user/pages/FirstLogin';
import Branch from './user/pages/BranchPage';
import ForgotPassword from './user/pages/ForgotPassword';
import ForgotPasswordOTP from './user/pages/ForgotPasswordOTP';
import ForgotPasswordSuccess from './user/pages/ForgotPasswordSuccess';
import ForgotPasswordSetNewPassword from './user/pages/ForgotPasswordSetNewPassword';
import Dashboard from './user/pages/DashboardPage';
import WeatherPage from './user/pages/WeatherPage';
import AlertPage from './user/pages/AlertPage';
import WaterLevelPage from './user/pages/WaterLevelPage';
import LightControl from './user/pages/LightControl';
import LEDScreen from './user/pages/LEDScreen';
import Fireworks from './user/pages/Fireworks';
import Wildlife from './user/pages/Wildlife';
import MapPage from './user/pages/MapPage';
import CctvPage from './user/pages/CctvPage';
import WastewaterPage from './user/pages/WastewaterPage.jsx';
import Report from './user/pages/ReportPage';
import ShowWeatherReport from './user/pages/ShowReport1';
import ShowWaterReport from './user/pages/ShowReport2';
import ShowwaterLevelStatus24HoursReport from './user/pages/ShowReport3';
import ShowPoleEnergyReport from './user/pages/ShowReport4';
import ShowPoleEnergy24HourReport from './user/pages/ShowReport5';
import ShowDetailedReport from './user/pages/ShowReport6';
import ShowUserReport from './user/pages/ShowReport7';
import ShowbranchesReport from './user/pages/ShowReport8';
import ShowBranchDetailReport from './user/pages/ShowReport9';
import MainPage from './admin/pages/MainPage';
import UserManagement from './admin/pages/UserManagement';
import BranchManagement from './admin/pages/BranchManagement';
import LightSwitch from './admin/pages/LightSwitch';
import ReportAdmin from './admin/pages/ReportPageAdmin';
import BranchDetails from './admin/pages/BranchDetails';
import PoleDetail from './admin/pages/PoleDetail';
import Sidebar from './components/Sidebar';
import SidebarAdmin from './components/SidebarAdmin';
import MapComponent from "./components/MapComponent";
import './App.css';
import './components/i18n.js';

function PageWithSidebar({ component: PageComponent }) {
  return (
    <div className='content'>
      <Sidebar />
      <PageComponent />
    </div>
  );
}

function PageWithSidebarAdmin({ component: PageComponent }) {
  return (
    <div className='content'>
      <SidebarAdmin />
      <PageComponent />
    </div>
  );
}

function getUserData() {
  return JSON.parse(sessionStorage.getItem('userData')) || {};
}

function getUserPermissions() {
  return JSON.parse(sessionStorage.getItem('permissions')) || {};
}

function getSelectedBranch() {
  return sessionStorage.getItem('selectedBranch');
}

function PrivateRoute({ isAdminRoute, permissionKey, element }) {
  const { admin } = getUserData();
  const permissions = getUserPermissions();
  const [delayCompleted, setDelayCompleted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayCompleted(true);
    }, 100); // Delay time in milliseconds

    return () => clearTimeout(timer);
  }, []);

  if (!delayCompleted) {
    return <div></div>; // Placeholder for loading state
  }

  if (admin === undefined) {
    return <Navigate to="/login" replace />;
  }

  if (isAdminRoute && !admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!isAdminRoute && admin) {
    return <Navigate to="/main" replace />;
  }

  if (!isAdminRoute && !getSelectedBranch()) {
    return <Navigate to="/branch" replace />;
  }

  if (!isAdminRoute && permissions[permissionKey] === false) {
    return <Navigate to="/dashboard" replace />;
  }

  return element;
}

function ProtectedRoute({ element }) {
  const { admin } = getUserData();

  if (admin !== undefined) {
    return <Navigate to={admin ? "/main" : "/dashboard"} replace />;
  }

  return element;
}

function FirstLoginRoute({ element }) {
  const { activated_on } = getUserData();

  if (activated_on !== null) {
    return <Navigate to="/dashboard" replace />;
  }

  return element;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<ProtectedRoute element={<Login />} />} />
        <Route path="/login/firstlogin" element={<FirstLoginRoute element={<FirstLogin />} />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/forgotpassword/OTP" element={<ForgotPasswordOTP />} />
        <Route path="/forgotpassword/setnewpassword" element={<ForgotPasswordSetNewPassword />} />
        <Route path="/forgotpassword/success" element={<ForgotPasswordSuccess />} />
        <Route path="/branch" element={<Branch />} />
        <Route path="/mapcomponent" element={<MapComponent />} />

        {/* admin Routes */}
        <Route path="/main" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={MainPage} />} />} />
        <Route path="/usermanagement" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={UserManagement} />} />} />
        <Route path="/branchmanagement" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={BranchManagement} />} />} />
        <Route path="/lightswitch" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={LightSwitch} />} />} />
        <Route path="/branchdetails" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={BranchDetails} />} />} />
        <Route path="/poledetails" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={PoleDetail} />} />} />
        <Route path="/reportadmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ReportAdmin} />} />} />
        <Route path="/report/showweatheradmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowWeatherReport} />} />} />
        <Route path="/report/showwateradmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowWaterReport} />} />} />
        <Route path="/report/showwaterleveladmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowwaterLevelStatus24HoursReport} />} />} />
        <Route path="/report/showpoleenergyadmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowPoleEnergyReport} />} />} />
        <Route path="/report/showpoleenergy24admin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowPoleEnergy24HourReport} />} />} />
        <Route path="/report/showdetailedreportadmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowDetailedReport} />} />} />
        <Route path="/report/showuserreportadmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowUserReport} />} />} />
        <Route path="/report/showbranchesadmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowbranchesReport} />} />} />
        <Route path="/report/showbranchdetailadmin" element={<PrivateRoute isAdminRoute={true} element={<PageWithSidebarAdmin component={ShowBranchDetailReport} />} />} />
        
        {/* user Routes */}
        <Route path="/dashboard" element={<PrivateRoute isAdminRoute={false} permissionKey="page_dashboard" element={<PageWithSidebar component={Dashboard} />} />} />
        <Route path="/weather" element={<PrivateRoute isAdminRoute={false} permissionKey="page_weather" element={<PageWithSidebar component={WeatherPage} />} />} />
        <Route path="/alert" element={<PrivateRoute isAdminRoute={false} permissionKey="page_alert" element={<PageWithSidebar component={AlertPage} />} />} />
        <Route path="/map" element={<PrivateRoute isAdminRoute={false} permissionKey="page_gis" element={<PageWithSidebar component={MapPage} />} />} />
        <Route path="/cctv" element={<PrivateRoute isAdminRoute={false} permissionKey="page_camera" element={<PageWithSidebar component={CctvPage} />} />} />
        <Route path="/lightcontrol" element={<PrivateRoute isAdminRoute={false} permissionKey="page_lighting" element={<PageWithSidebar component={LightControl} />} />} />
        <Route path="/led" element={<PrivateRoute isAdminRoute={false} permissionKey="page_display" element={<PageWithSidebar component={LEDScreen} />} />} />
        <Route path="/fireworks" element={<PrivateRoute isAdminRoute={false} permissionKey="page_fireworks" element={<PageWithSidebar component={Fireworks} />} />} />
        <Route path="/waterlevel" element={<PrivateRoute isAdminRoute={false} permissionKey="page_waterlevel" element={<PageWithSidebar component={WaterLevelPage} />} />} />
        <Route path="/wildlife" element={<PrivateRoute isAdminRoute={false} permissionKey="page_wildlife" element={<PageWithSidebar component={Wildlife} />} />} />
        <Route path="/wastewater" element={<PrivateRoute isAdminRoute={false} permissionKey="page_wastewater" element={<PageWithSidebar component={WastewaterPage} />} />} />
        <Route path="/report" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report" element={<PageWithSidebar component={Report} />} />} />
        <Route path="/report/showweather" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showweather" element={<PageWithSidebar component={ShowWeatherReport} />} />} />
        <Route path="/report/showwater" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showwater" element={<PageWithSidebar component={ShowWaterReport} />} />} />
        <Route path="/report/showwaterlevel" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showwaterlevel" element={<PageWithSidebar component={ShowwaterLevelStatus24HoursReport} />} />} />
        <Route path="/report/showpoleenergy" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showpoleenergy" element={<PageWithSidebar component={ShowPoleEnergyReport} />} />} />
        <Route path="/report/showpoleenergy24" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showpoleenergy24" element={<PageWithSidebar component={ShowPoleEnergy24HourReport} />} />} />
        <Route path="/report/showdetailedreport" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showdetailedreport" element={<PageWithSidebar component={ShowDetailedReport} />} />} />
        <Route path="/report/showuserreport" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showuserreport" element={<PageWithSidebar component={ShowUserReport} />} />} />
        <Route path="/report/showbranches" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showbranches" element={<PageWithSidebar component={ShowbranchesReport} />} />} />
        <Route path="/report/showbranchdetail" element={<PrivateRoute isAdminRoute={false} permissionKey="page_report_showbranchdetail" element={<PageWithSidebar component={ShowBranchDetailReport} />} />} />
      </Routes>
    </Router>
  );
}

export default App;
